import { useEffect, useState } from 'react';
import { loginAndGetToken, getToken, isTokenValid } from '../api/auth';

export interface UserInfo {
  identityProvider: string;
  userId: string;
  userDetails: string;
  userRoles: string[];
  email: string;
  name: string;
}

function getClaimValueFromArray(claimArr: unknown, keys: string[]): string | undefined {
  if (!Array.isArray(claimArr)) return undefined;
  for (const item of claimArr) {
    if (!item || typeof item !== 'object') continue;
    const rec = item as Record<string, unknown>;
    for (const k of keys) {
      const claimType = rec['type'];
      if (typeof claimType === 'string' && claimType.toLowerCase() === k.toLowerCase()) {
        const val = rec['value'];
        if (typeof val === 'string') return val;
      }
      // sometimes the claim may be keyed directly by name
      const direct = rec[k];
      if (typeof direct === 'string') return direct;
    }
  }
  return undefined;
}

export const useAuth = () => {
  const [user, setUser] = useState<UserInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAllowed, setIsAllowed] = useState<boolean | null>(null);
  const [token, setToken] = useState<string | null>(null);

  useEffect(() => {
    const fetchUser = async () => {
      // DEV MODE: Skip SWA auth only in development
      const skipAuth = import.meta.env.DEV && import.meta.env.VITE_DEV_SKIP_AUTH === 'true';

      if (skipAuth) {
        const devUser: UserInfo = {
          identityProvider: 'dev',
          userId: 'dev-user-123',
          userDetails: 'dev@localhost',
          userRoles: ['authenticated'],
          email: 'dev@localhost',
          name: 'Dev User',
        };

        // Create a JSON token with user data (backend will parse this in dev mode)
        const devTokenPayload = {
          email: devUser.email,
          name: devUser.name,
          sub: devUser.email,
          iat: Math.floor(Date.now() / 1000),
          exp: Math.floor(Date.now() / 1000) + (1 * 60 * 60), // 1h
        };
        const devToken = JSON.stringify(devTokenPayload);

        // Save token to sessionStorage (needed by getToken())
        sessionStorage.setItem('api_token', devToken);

        setUser(devUser);
        setIsAllowed(true);
        setToken(devToken);
        setLoading(false);
        return;
      }

      try {
        const response = await fetch('/.auth/me');
        const dataRaw = (await response.json()) as unknown;

        if (!dataRaw || typeof dataRaw !== 'object') {
          setUser(null);
          setIsAllowed(null);
          setToken(null);
          setLoading(false);
          return;
        }

        const dataRecord = dataRaw as Record<string, unknown>;
        const clientPrincipal = dataRecord['clientPrincipal'];
        if (!clientPrincipal || typeof clientPrincipal !== 'object') {
          setUser(null);
          setIsAllowed(null);
          setToken(null);
          setLoading(false);
          return;
        }

        const cpRecord = clientPrincipal as Record<string, unknown>;

        const identityProvider = typeof cpRecord['identityProvider'] === 'string' ? cpRecord['identityProvider'] : '';
        const userId = typeof cpRecord['userId'] === 'string' ? cpRecord['userId'] : '';
        const userDetails = typeof cpRecord['userDetails'] === 'string' ? cpRecord['userDetails'] : '';
        const userRoles = Array.isArray(cpRecord['userRoles'])
          ? (cpRecord['userRoles'] as unknown[]).filter((r) => typeof r === 'string') as string[]
          : [];

        // SWA sometimes uses 'claims' or 'userClaims'; accept either only if it's an array
        const possibleClaims = cpRecord['claims'] ?? cpRecord['userClaims'];

        // Only these claim keys are used by product requirements
        let email = getClaimValueFromArray(possibleClaims, ['email']);
        const name = getClaimValueFromArray(possibleClaims, ['name', 'given_name', 'preferred_username']);

        // Fallback: if email claim not present but userDetails looks like an email, use it
        if (!email && typeof userDetails === 'string' && userDetails.includes('@')) {
          email = userDetails;
        }

        const mapped: UserInfo = {
          identityProvider,
          userId,
          userDetails,
          userRoles,
          email: email ?? '',
          name: name ?? '',
        };

        // Server-side allowlist check: fetch allowed status from the SWA function BEFORE setting user
        try {
          const allowRes = await fetch('/api/check-access');
          
          if (allowRes.ok) {
            try {
              const allowJson = await allowRes.json();
              const allowedFlag = allowJson && typeof allowJson.allowed === 'boolean' ? allowJson.allowed : false;
              
              if (!allowedFlag) {
                // User not on allowlist - set state but don't set user
                setIsAllowed(false);
                setUser(null);
                setToken(null);
                setLoading(false);
                return;
              }
              
              // Only set user and allowed if check passed
              setIsAllowed(true);
              setUser(mapped);
              
              // Fetch JWT token after successful authentication
              // Check if we have a valid token, otherwise get a new one
              const existingToken = getToken();

              if (existingToken && isTokenValid()) {
                setToken(existingToken);
              } else {
                const newToken = await loginAndGetToken();
                setToken(newToken);
              }
            } catch (jsonError) {
              setIsAllowed(false);
              setUser(null);
              setToken(null);
              setLoading(false);
              return;
            }
          } else {
            // If endpoint fails, deny access for security
            setIsAllowed(false);
            setUser(null);
            setToken(null);
            setLoading(false);
            return;
          }
        } catch (err) {
          // If allowlist endpoint fails, deny access for security (fail-closed)
          setIsAllowed(false);
          setUser(null);
          setToken(null);
          setLoading(false);
          return;
        }
      } catch (error) {
        setUser(null);
        setToken(null);
      } finally {
        setLoading(false);
      }
    };

    fetchUser();
  }, []);

  return { user, loading, isAuthenticated: !!user, isAllowed, token };
};